import { useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DIRECT_SALE_UPLOAD_ACCEPT,
  DIRECT_SALE_UPLOAD_MAX_MB as MAX_SIZE_MB,
  useDirectSaleUpload,
} from '../useDirectSaleUpload';

/**
 * The upload drawer of the desktop editor: the same file handling as the
 * phone's panel (useDirectSaleUpload decides what happens to the file), in
 * the calmer shape of Spreadshirt's designer — one icon, one line, one
 * full-width button, the small print below a rule.
 *
 * The whole drawer is the drop target, not a dashed box inside it: with a
 * 300 px drawer a box inside it would be too small to hit while dragging.
 * The drawer stays open while the background upload runs and closes on
 * success.
 */
export function DirectSaleDesktopUploadPanel({
  onClose,
}: {
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { handleFile, isUploading, error } = useDirectSaleUpload({
    onDone: onClose,
  });

  // dragenter/dragleave fire for every child the pointer crosses; only the
  // balance tells whether the file is still over the drawer. Without the
  // counter the highlight would flicker on every icon and line.
  const dragDepth = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragOver(true);
  };
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragOver(false);
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && !isUploading) void handleFile(file);
  };

  return (
    <div
      data-testid="desktop-upload-dropzone"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      className={cn(
        'flex flex-1 flex-col px-6 py-4 transition-colors',
        isDragOver && 'bg-primary-50'
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={DIRECT_SALE_UPLOAD_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleFile(file);
        }}
      />

      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <ImagePlus
          aria-hidden="true"
          className="h-14 w-14 text-ink-3"
          strokeWidth={1.25}
        />
        <p className="text-sm font-semibold text-ink">
          {t('directSale.design.uploadPanel.dropHint')}
        </p>
        <p className="text-[11px] uppercase tracking-wider text-mute">
          {t('directSale.desktop.startOr')}
        </p>
        <button
          type="button"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
          className="h-12 w-full rounded-control bg-primary text-sm font-semibold text-primary-fg transition-all hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isUploading
            ? t('directSale.design.uploadPanel.uploading')
            : t('directSale.design.chooseFile')}
        </button>
      </div>

      {error && (
        <p role="alert" className="text-xs font-medium text-danger">
          {t(`directSale.design.uploadPanel.error.${error}`, {
            size: MAX_SIZE_MB,
          })}
        </p>
      )}

      <div className="mt-4 border-t border-line pt-4">
        <p className="text-xs text-mute">
          {t('directSale.design.uploadPanel.formats', { size: MAX_SIZE_MB })}
        </p>
        <p className="mt-1 text-xs text-mute">
          {t('directSale.design.uploadPanel.minRes')}
        </p>
        <p className="mt-3 text-xs leading-relaxed text-ink-3">
          {t('directSale.design.uploadPanel.afterUpload')}
        </p>
      </div>
    </div>
  );
}
